// [PURPOSE]: Inventory Context - Centralized inventory and vault state management
// [WHY]: Extract inventory-related state from monolithic App.jsx (16,437 lines)
// [INTEGRATION]: Provides inventory, vaults, drag/drop, and item management via React Context
// [CREATED]: October 24, 2025 - Phase 4A: Architectural Refactoring
// [UPDATED]: October 25, 2025 - Phase 4B Step 6: Added cross-context dependencies and equipment/item functions

import React, { createContext, useContext, useState, useCallback, useRef, useMemo, useEffect } from 'react';
import { systemBus, EVENTS } from '../services/systemBus';
import { BASE_ITEMS } from '../data/gameData';
import { effectCalculator } from '../services/effectCalculator';
import { effectRegistry } from '../services/effectRegistry'; // [PHASE 1.3 - NOV 14, 2025]: EffectRegistry integration for item effects
import { applyRechargeForDays } from '../services/itemRechargeService'; // [R1.D2.6]: Pure recharge service
import { buildItemRegistrationsFromInventory } from '../utils/itemEffectsRegistration'; // [BATCH 14 / P0 C-8]: Shared helper for re-registration tests
import { getMaxAttunement } from '../utils/attunementUtils'; // [STRUCT-C 2026-05-16]: Artificer attunement-cap scaling (replaces hardcoded 3)
import { meetsAttunementRequirement } from '../utils/attunementRequirements'; // [STRUCT-C 2026-05-16]: Class/race/level/spellcaster gate + Magic Item Savant bypass
import { canUnattuneItem, getCurseUnattuneBlockedMessage } from '../utils/cursedItemGates'; // [SUB-PLAN 1C ITEM 4 / 2026-05-23]: RAW DMG p.138 cursed-attunement lock
import { isItemIdentified } from '../utils/itemIdentificationGate'; // [SUB-PLAN 1C ITEM 3 / 2026-05-23]: Unidentified-loot flag gate
import { expandRingEquipSlots } from '../utils/equipSlotMigration'; // [SUB-PLAN 1R-RENDERGAP STONE 1 / 2026-05-29]: ring/earring bare-slot → ring1+ring2/earring1+earring2 expansion
import { useCharacter } from './CharacterContext';
import { useUI } from './UIContext';
import { useSettings } from './SettingsContext';

/**
 * InventoryContext provides all inventory-related state and functions
 *
 * This context manages:
 * - Inventory array (items in character's main bag)
 * - Vaults array (storage locations with positions)
 * - Drag & drop state (draggedItem, dragSource, dragPosition)
 * - Item selection state (for tap-to-move mode)
 * - Hover state (for visual feedback during drag)
 * - Container management (openContainer, expandedContainers)
 * - Item editing dialogs and modals
 * - Sorting, filtering, and search
 * - Context menus
 */
const InventoryContext = createContext(null);

export const InventoryProvider = ({ children }) => {
  // ============================================================================
  // CROSS-CONTEXT DEPENDENCIES
  // ============================================================================
  // [PURPOSE]: Access functions from other contexts for equipment/item management
  // [WHY]: Equipment functions need to log activity, show status, update currency, etc.

  const {
    playerStats,
    logActivity,
    activeQuickBonuses,
    setActiveQuickBonuses,
    currencies,
    setCurrencies
  } = useCharacter();

  const { showStatus } = useUI();
  const { settings } = useSettings();

  // ============================================================================
  // CORE INVENTORY STATE
  // ============================================================================

  const [inventory, setInventory] = useState([]);
  const [vaults, setVaults] = useState([]);

  // ============================================================================
  // DRAG & DROP STATE
  // ============================================================================

  const [draggedItem, setDraggedItem] = useState(null);
  const [dragSource, setDragSource] = useState(null);
  const [dragPosition, setDragPosition] = useState(null);

  // For tap-to-move mode (mobile)
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedItemSource, setSelectedItemSource] = useState(null);

  // Hover state for visual feedback
  const [hoveredGrid, setHoveredGrid] = useState(null);
  const [hoveredCell, setHoveredCell] = useState(null);

  // Ref for drag data (persistent across renders)
  const dragDataRef = useRef({
    item: null,
    source: null,
    isDragging: false
  });

  // ============================================================================
  // CONTAINER & VAULT STATE
  // ============================================================================

  const [openContainer, setOpenContainer] = useState(null);
  const [showVaultModal, setShowVaultModal] = useState(null);
  const [expandedContainers, setExpandedContainers] = useState(new Set());
  const [editingVault, setEditingVault] = useState(null);
  const [directAddToVault, setDirectAddToVault] = useState(null);

  // ============================================================================
  // ITEM MANAGEMENT STATE
  // ============================================================================

  const [editingItem, setEditingItem] = useState(null);
  const [showItemDialog, setShowItemDialog] = useState(false);
  const [showVaultDialog, setShowVaultDialog] = useState(false);
  const [contextMenu, setContextMenu] = useState(null);

  // ============================================================================
  // SORTING & FILTERING STATE
  // ============================================================================

  const [sortBy, setSortBy] = useState('name'); // name, weight, value, type
  const [sortOrder, setSortOrder] = useState('asc'); // asc, desc
  const [filterType, setFilterType] = useState('all'); // all, weapons, armor, consumables, containers, misc
  const [searchQuery, setSearchQuery] = useState('');

  // Magic items panel filters
  const [magicItemsFilter, setMagicItemsFilter] = useState('activatable'); // Filter for magic items panel
  const [magicItemsSort, setMagicItemsSort] = useState('name'); // Sort option for magic items

  // ============================================================================
  // EFFECT REGISTRY RE-HYDRATION
  // ============================================================================
  //
  // [FIX phase6-E E-3]: On mount and whenever the inventory list changes,
  // re-register the effects of all currently-equipped items. CharacterContext
  // calls effectRegistry.clear() during its own re-registration pass, so
  // without this useEffect equipped item effects (Belt of Giant Strength STR=21,
  // Bracers of Defense +2 AC, etc.) silently disappear after a page reload —
  // the item still shows equipped in the UI, but its registry source-id is
  // gone until the player unequips and re-equips. This was the canonical-
  // path bug: CharacterContext was iterating `playerStats.equipment?.equipped`
  // (a property that does not exist) instead of the canonical inventory
  // array each item lives in.
  //
  // Re-registration is idempotent: register() removes any prior entry under
  // the same sourceId before inserting, so this useEffect is safe to fire on
  // every inventory change.
  useEffect(() => {
    // [BATCH 14 / P0 C-8 — APR 2026]: Pure helper extracted to
    //   src/utils/itemEffectsRegistration.js so the regression test
    //   exercises the same logic this useEffect runs. Pre-fix the
    //   test had its own inline replication and could pass while
    //   production drifted.
    const registrations = buildItemRegistrationsFromInventory(inventory);
    registrations.forEach(({ sourceId, effects, item }) => {
      try {
        effectRegistry.register(sourceId, effects, item);
      } catch (error) {
        console.warn(`[InventoryContext] Failed to re-register effects for ${item?.name}:`, error);
      }
    });
    // Force a recalc after re-registration so dependent UI picks up the new state.
    effectCalculator.clearCache();
  }, [inventory]);

  // [R1.D2.6]: Recharge time-tagged items (dawn/daily/weekly/monthly) on every
  // day-advance, regardless of which path triggered it (long rest, calendar
  // manual, downtime listener, future). Previously this lived inline inside
  // CalendarPanel.jumpToDate, so long rests and downtime completions never
  // recharged dawn items. The pure function applyRechargeForDays lives in
  // itemRechargeService for testability; this effect owns React state +
  // ITEM_RECHARGED event emission.
  useEffect(() => {
    const unsub = systemBus.on(EVENTS.DAY_ADVANCED, ({ days, currentTime }) => {
      setInventory((prev) => {
        // [GAME-CLOCK 2026-07-13]: pass the in-game clock (payload currentTime) so
        // lastRecharged is stamped with game time, not real wall-clock (Rule 20).
        const { updatedInventory, rechargeEvents } = applyRechargeForDays(prev, days, currentTime);
        // Emit recharge events AFTER state update so listeners see the new state.
        if (rechargeEvents.length > 0) {
          // Defer to the next tick so React batching settles.
          queueMicrotask(() => {
            rechargeEvents.forEach((evt) => {
              systemBus.emit(EVENTS.ITEM_RECHARGED, evt);
            });
          });
        }
        return updatedInventory;
      });
    });
    return unsub;
  }, []); // empty deps — setInventory is stable, systemBus is module-level

  // ============================================================================
  // INVENTORY ACTIONS
  // ============================================================================

  /**
   * Add item to inventory
   *
   * [PASS-D APR 30 2026]: Honors `item.maxStack` ceiling. If the existing
   *   stack would exceed maxStack, fill it to maxStack and spill the
   *   remainder into a new stack. If the incoming `quantity` itself
   *   exceeds maxStack, split into multiple full-size stacks plus a final
   *   remainder stack. Items without `maxStack` accept unbounded merges
   *   (backward-compat).
   *
   * @param {Object} item - Item object to add
   * @param {number} quantity - Number of items to add (for stackable items)
   */
  const addItem = useCallback((item, quantity = 1) => {
    setInventory(prev => {
      const maxStack = (typeof item.maxStack === 'number' && item.maxStack > 0)
        ? item.maxStack
        : Infinity;
      let remaining = quantity;
      let next = [...prev];
      const stackedEvents = [];
      const freshEvents = [];

      // Helper for fresh-item creation. Generates a unique id so multiple
      // overflow stacks created in a single addItem call don't collide.
      const makeFresh = (qtyForThisStack, suffix = '') => ({
        ...item,
        id: (item.id ? `${item.id}${suffix}` : `${item.name}_${Date.now()}${suffix}_${Math.random()}`),
        quantity: item.stackable ? qtyForThisStack : 1,
        position: item.position || null,
      });

      // [STACKABLE PATH]: Try to merge into existing stack (if any), then
      // overflow into new stacks until `remaining` is exhausted.
      if (item.stackable && remaining > 0) {
        const existingIndex = next.findIndex(i =>
          i.name === item.name && i.category === item.category
        );
        if (existingIndex !== -1) {
          const existing = next[existingIndex];
          const existingQty = existing.quantity || 1;
          const room = Math.max(0, maxStack - existingQty);
          const topUp = Math.min(room, remaining);
          if (topUp > 0) {
            const merged = { ...existing, quantity: existingQty + topUp };
            next[existingIndex] = merged;
            remaining -= topUp;
            stackedEvents.push({ entry: merged, qty: topUp });
          }
        }

        // Spill any remaining quantity into new full-size stacks.
        let overflowIdx = 0;
        while (remaining > 0) {
          const qtyForThisStack = Math.min(maxStack, remaining);
          const fresh = makeFresh(qtyForThisStack, `_o${overflowIdx++}`);
          next.push(fresh);
          freshEvents.push({ entry: fresh, qty: qtyForThisStack });
          remaining -= qtyForThisStack;
        }
      } else {
        // [NON-STACKABLE PATH]: Always create a fresh entry, qty:1.
        const fresh = makeFresh(1);
        next.push(fresh);
        freshEvents.push({ entry: fresh, qty: 1 });
      }

      // Emit one event per stack touched. This preserves the prior
      // single-stack contract for the most common case while still
      // surfacing overflow stacks to listeners.
      stackedEvents.forEach(({ entry, qty }) => {
        systemBus.emit(EVENTS.INVENTORY_ITEM_ADDED, {
          item: entry,
          quantity: qty,
          source: 'inventory',
          stacked: true,
        });
        logActivity(`Added ${qty}x ${item.name} (stacked)`, 'items', {
          item: item.name, quantity: qty, stacked: true,
        });
      });
      freshEvents.forEach(({ entry, qty }) => {
        systemBus.emit(EVENTS.INVENTORY_ITEM_ADDED, {
          item: entry,
          quantity: qty,
          source: 'inventory',
          stacked: false,
        });
        logActivity(`Added ${item.name} to inventory${qty > 1 ? ` (x${qty})` : ''}`, 'items', {
          item: item.name, quantity: qty,
        });
      });

      return next;
    });
  }, [logActivity]);

  /**
   * Remove item from inventory
   * @param {string} itemId - ID of item to remove
   * @param {number} quantity - Number of items to remove (for stackable items)
   */
  const removeItem = useCallback((itemId, quantity = 1) => {
    // [FIX P4-12]: Check for contained currency BEFORE the setInventory call
    // [WHY]: Can't call setCurrencies inside setInventory's functional updater
    const itemToRemove = inventory.find(i => i.id === itemId);
    if (itemToRemove?.containsCurrency) {
      const { amount, type } = itemToRemove.containsCurrency;
      if (amount && type) {
        setCurrencies(prev => ({
          ...prev,
          [type]: (prev[type] || 0) + amount
        }));
        logActivity(`Recovered ${amount} ${type} from removed ${itemToRemove.name}`, 'currency', {
          amount, type, item: itemToRemove.name
        });
      }
    }

    setInventory(prev => {
      const itemIndex = prev.findIndex(i => i.id === itemId);
      if (itemIndex === -1) return prev;

      const item = prev[itemIndex];

      // [INTEGRATION]: Emit INVENTORY_ITEM_REMOVED event
      systemBus.emit(EVENTS.INVENTORY_ITEM_REMOVED, {
        item,
        quantity,
        source: 'inventory'
      });

      // [v1.1]: Log item removal
      logActivity(`Removed ${item.name}${quantity > 1 ? ` (x${quantity})` : ''} from inventory`, 'items', { item: item.name, quantity });

      // If stackable and quantity > 1, reduce quantity
      if (item.stackable && item.quantity > quantity) {
        const updated = [...prev];
        updated[itemIndex] = {
          ...item,
          quantity: item.quantity - quantity
        };
        return updated;
      }

      // Remove item completely
      return prev.filter(i => i.id !== itemId);
    });
  }, [logActivity, inventory, setCurrencies]);

  /**
   * Update item properties
   * @param {string} itemId - ID of item to update
   * @param {Object} updates - Properties to update
   */
  const updateItem = useCallback((itemId, updates) => {
    setInventory(prev => {
      const itemIndex = prev.findIndex(i => i.id === itemId);
      if (itemIndex === -1) return prev;

      const updated = [...prev];
      updated[itemIndex] = {
        ...updated[itemIndex],
        ...updates
      };

      return updated;
    });
  }, []);

  /**
   * Split a stack into two stacks.
   *
   * [STACK INVESTIGATION APR 30 2026]: Player-facing operation surfaced
   *   from the context menu. Reduces the source stack by `splitCount` and
   *   creates a new stack with the same item data and that quantity. The
   *   new stack inherits no position so the next render places it in the
   *   first available cell (existing behavior for unpositioned items).
   *
   * @param {Object} item - Stackable item with quantity > 1
   * @param {number} splitCount - How many to remove into the new stack
   *   (must be >= 1 and < item.quantity)
   */
  const splitStack = useCallback((item, splitCount) => {
    if (!item || !item.stackable) return;
    const currentQty = item.quantity || 1;
    const count = parseInt(splitCount, 10);
    if (!Number.isFinite(count) || count < 1 || count >= currentQty) {
      showStatus(`Cannot split: pick a number between 1 and ${currentQty - 1}.`);
      return;
    }
    setInventory(prev => {
      const idx = prev.findIndex(i => i.id === item.id);
      if (idx === -1) return prev;
      const source = prev[idx];
      const remaining = (source.quantity || 1) - count;
      if (remaining < 1) return prev;

      const newStack = {
        ...source,
        id: `${source.id}_split_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`,
        quantity: count,
        // Drop position so the grid places the new stack on next render.
        position: null,
        equipped: false,
        equipSlot: null,
      };
      const next = [...prev];
      next[idx] = { ...source, quantity: remaining };
      next.push(newStack);

      systemBus.emit(EVENTS.INVENTORY_ITEM_ADDED, {
        item: newStack,
        quantity: count,
        source: 'inventory',
        stacked: false,
        reason: 'split',
      });
      return next;
    });
    logActivity(`Split ${item.name} stack: ${count} into a new stack`, 'items', {
      item: item.name, splitCount: count, originalQuantity: currentQty,
    });
    showStatus(`Split ${count} ${item.name} into a new stack.`);
  }, [logActivity, showStatus]);

  /**
   * Flip an item's stackable flag from false to true.
   *
   * [STACK INVESTIGATION APR 30 2026]: Surfaced from the context menu
   *   so a player who right-clicks a dagger can opt into stacking
   *   without having to discover the EditItemDialog toggle on their
   *   own. Mirrors the per-item override added in EditItemDialog.
   *
   * @param {Object} item - Item to flip stackable: true on
   */
  const makeStackable = useCallback((item) => {
    if (!item || item.stackable) return;
    setInventory(prev => {
      const idx = prev.findIndex(i => i.id === item.id);
      if (idx === -1) return prev;
      const next = [...prev];
      next[idx] = {
        ...next[idx],
        stackable: true,
        quantity: next[idx].quantity || 1,
      };
      return next;
    });
    logActivity(`${item.name} marked stackable`, 'items', { item: item.name });
  }, [logActivity]);

  /**
   * Merge a source stack onto a target stack (drag-merge primitive).
   *
   * [STACK INVESTIGATION APR 30 2026]: When the user drags one stackable
   *   item onto another identical stackable item in the grid, the prior
   *   behavior was "tile occupied — drop rejected" via isValidPosition.
   *   This primitive lets the drop handler check identity + invoke a
   *   merge instead of rejecting.
   *
   *   Honors maxStack on the target. Returns true if merged (any amount),
   *   false if not applicable (different items, non-stackable, missing IDs).
   *
   * @param {string} sourceId - ID of the dragged stack
   * @param {string} targetId - ID of the stack being dropped onto
   * @returns {boolean} true if a merge occurred
   */
  const mergeStackOnto = useCallback((sourceId, targetId) => {
    if (!sourceId || !targetId || sourceId === targetId) return false;
    // Read current inventory via the closure (`inventory` from useState).
    // The dragged-onto-identical drop happens synchronously after the
    // user releases the mouse, so the inventory reference is fresh enough
    // to do the identity + capacity check before issuing the setState.
    const sourceIdx = inventory.findIndex(i => i.id === sourceId);
    const targetIdx = inventory.findIndex(i => i.id === targetId);
    if (sourceIdx === -1 || targetIdx === -1) return false;

    const source = inventory[sourceIdx];
    const target = inventory[targetIdx];

    if (!source.stackable || !target.stackable) return false;
    if (source.name !== target.name) return false;
    if (source.category !== target.category) return false;

    const maxStack = (typeof target.maxStack === 'number' && target.maxStack > 0)
      ? target.maxStack
      : Infinity;
    const targetQty = target.quantity || 1;
    const sourceQty = source.quantity || 1;
    const room = Math.max(0, maxStack - targetQty);
    if (room <= 0) return false;
    const moved = Math.min(room, sourceQty);
    if (moved <= 0) return false;

    setInventory(prev => {
      const sIdx = prev.findIndex(i => i.id === sourceId);
      const tIdx = prev.findIndex(i => i.id === targetId);
      if (sIdx === -1 || tIdx === -1) return prev;
      const s = prev[sIdx];
      const t = prev[tIdx];
      // Re-validate against the current state (defensive — concurrent
      // edits could change quantities between the validation read and
      // the setState).
      if (!s.stackable || !t.stackable) return prev;
      if (s.name !== t.name || s.category !== t.category) return prev;

      const tCap = (typeof t.maxStack === 'number' && t.maxStack > 0) ? t.maxStack : Infinity;
      const tQty = t.quantity || 1;
      const sQty = s.quantity || 1;
      const tRoom = Math.max(0, tCap - tQty);
      if (tRoom <= 0) return prev;
      const m = Math.min(tRoom, sQty);
      const newTargetQty = tQty + m;
      const newSourceQty = sQty - m;

      const next = [...prev];
      next[tIdx] = { ...t, quantity: newTargetQty };
      if (newSourceQty <= 0) {
        next.splice(sIdx, 1);
      } else {
        const adj = sIdx > tIdx ? sIdx : sIdx;
        next[adj] = { ...s, quantity: newSourceQty };
      }

      systemBus.emit(EVENTS.INVENTORY_ITEM_MOVED, {
        item: next[tIdx],
        source: 'inventory',
        destination: 'inventory',
        reason: 'merge',
      });
      return next;
    });

    logActivity(`Merged ${moved}× ${target.name}`, 'items', {
      sourceId, targetId, mergedQuantity: moved,
    });
    return true;
  }, [inventory, logActivity]);

  /**
   * Combine the source stack with all matching identical stacks in the
   * inventory. Honors maxStack ceiling — if the merged total exceeds it,
   * the destination fills to maxStack and the rest stays in subsequent
   * stacks (proportionally). Items with no maxStack merge unbounded.
   *
   * [STACK INVESTIGATION APR 30 2026]: Surfaced from the context menu so
   *   a player who has accidentally created two stacks of arrows can
   *   combine them without dragging.
   *
   * @param {Object} sourceItem - The item the menu was opened on
   */
  const combineStacks = useCallback((sourceItem) => {
    if (!sourceItem || !sourceItem.stackable) return;
    setInventory(prev => {
      const matches = prev.filter(i =>
        i.stackable &&
        i.name === sourceItem.name &&
        i.category === sourceItem.category &&
        i.id !== sourceItem.id
      );
      if (matches.length === 0) return prev;

      const sourceIdx = prev.findIndex(i => i.id === sourceItem.id);
      if (sourceIdx === -1) return prev;
      const source = prev[sourceIdx];
      const maxStack = (typeof source.maxStack === 'number' && source.maxStack > 0)
        ? source.maxStack
        : Infinity;

      // Greedy merge into source up to maxStack, leaving remainder in matches.
      let mergedTotal = source.quantity || 1;
      const ids = new Set();
      const trimmedMatches = [];
      for (const m of matches) {
        const room = Math.max(0, maxStack - mergedTotal);
        if (room <= 0) {
          // Source is full; preserve match unchanged.
          continue;
        }
        const fromMatch = Math.min(room, m.quantity || 1);
        mergedTotal += fromMatch;
        const matchRemaining = (m.quantity || 1) - fromMatch;
        if (matchRemaining <= 0) {
          ids.add(m.id);
        } else {
          trimmedMatches.push({ id: m.id, quantity: matchRemaining });
        }
      }

      const next = prev
        .filter(i => !ids.has(i.id))
        .map(i => {
          if (i.id === source.id) return { ...i, quantity: mergedTotal };
          const trim = trimmedMatches.find(t => t.id === i.id);
          if (trim) return { ...i, quantity: trim.quantity };
          return i;
        });
      return next;
    });
    logActivity(`Combined ${sourceItem.name} stacks`, 'items', { item: sourceItem.name });
    showStatus(`Combined ${sourceItem.name} stacks.`);
  }, [logActivity, showStatus]);

  /**
   * Move item to new position
   * @param {string} itemId - ID of item to move
   * @param {Array} newPosition - New [x, y] position
   */
  const moveItem = useCallback((itemId, newPosition) => {
    setInventory(prev => {
      const itemIndex = prev.findIndex(i => i.id === itemId);
      if (itemIndex === -1) return prev;

      const item = prev[itemIndex];
      const oldPosition = item.position;

      const updated = [...prev];
      updated[itemIndex] = {
        ...item,
        position: newPosition
      };

      // [INTEGRATION]: Emit INVENTORY_ITEM_MOVED event
      systemBus.emit(EVENTS.INVENTORY_ITEM_MOVED, {
        item: updated[itemIndex],
        oldPosition,
        newPosition,
        source: 'inventory',
        destination: 'inventory'
      });

      return updated;
    });
  }, []);

  /**
   * Transfer item to vault
   * @param {Object} item - Item to transfer
   * @param {string} vaultId - ID of vault to transfer to
   * @param {Array} position - Position in vault [x, y]
   */
  const transferToVault = useCallback((item, vaultId, position = null) => {
    // Remove from inventory
    setInventory(prev => prev.filter(i => i.id !== item.id));

    // Add to vault — merge into a matching stack if present.
    // [BATCH 4 / P1 D-22 — APR 2026]: Pre-fix this always pushed a new entry
    //   regardless of existing stacks, so depositing arrows N times produced
    //   N separate stacks. Now mirrors inventory's mergeStackOnto rules.
    let mergedQty = null;
    setVaults(prev => {
      const vaultIndex = prev.findIndex(v => v.id === vaultId);
      if (vaultIndex === -1) return prev;
      const vault = prev[vaultIndex];

      // Try to merge into an existing identical stack
      const existingItems = vault.items || [];
      let nextItems = existingItems;
      let didMerge = false;
      if (item.stackable) {
        const matchIdx = existingItems.findIndex(i =>
          i.stackable &&
          i.name === item.name &&
          i.category === item.category
        );
        if (matchIdx !== -1) {
          const target = existingItems[matchIdx];
          const tCap = (typeof target.maxStack === 'number' && target.maxStack > 0)
            ? target.maxStack : Infinity;
          const tQty = target.quantity || 1;
          const sQty = item.quantity || 1;
          const room = Math.max(0, tCap - tQty);
          const moved = Math.min(room, sQty);
          if (moved > 0) {
            const merged = { ...target, quantity: tQty + moved };
            nextItems = [...existingItems];
            nextItems[matchIdx] = merged;
            // Carry remainder if source overflowed; in practice the whole
            // item is being transferred so any leftover stays as a NEW entry.
            if (sQty - moved > 0) {
              nextItems.push({ ...item, quantity: sQty - moved, position, vaultId });
            }
            mergedQty = moved;
            didMerge = true;
          }
        }
      }

      if (!didMerge) {
        const vaultItem = { ...item, position, vaultId };
        nextItems = [...existingItems, vaultItem];
      }

      const updated = [...prev];
      updated[vaultIndex] = { ...vault, items: nextItems };

      // [INTEGRATION]: Emit INVENTORY_VAULT_TRANSFER event
      systemBus.emit(EVENTS.INVENTORY_VAULT_TRANSFER, {
        item,
        fromSource: 'inventory',
        toVault: vaultId,
        position,
        merged: didMerge,
        mergedQty: didMerge ? mergedQty : 0,
      });

      return updated;
    });

    // [BATCH 4 / P1 D-23]: Direct logActivity call — was previously
    //   relying on a VaultModal subscriber that doesn't fire if the modal
    //   is unmounted between dispatch and re-render.
    const qty = item.quantity || 1;
    logActivity(
      `Stored ${qty}× ${item.name} in vault`,
      'items',
      { item: item.name, quantity: qty, vaultId, merged: mergedQty !== null }
    );
  }, [logActivity]);

  /**
   * Transfer item from vault to inventory
   * @param {Object} item - Item to transfer
   * @param {string} vaultId - ID of vault to transfer from
   */
  const transferFromVault = useCallback((item, vaultId) => {
    // Remove from vault
    setVaults(prev => {
      const vaultIndex = prev.findIndex(v => v.id === vaultId);
      if (vaultIndex === -1) return prev;

      const vault = prev[vaultIndex];
      const updated = [...prev];
      updated[vaultIndex] = {
        ...vault,
        items: vault.items.filter(i => i.id !== item.id)
      };

      return updated;
    });

    // Add to inventory — merge into matching stack if present.
    // [BATCH 4 / P1 D-22 — APR 2026]: Mirrors transferToVault merge rules.
    let didMerge = false;
    let mergedQty = null;
    setInventory(prev => {
      const inventoryItem = { ...item, position: null, vaultId: undefined };

      if (item.stackable) {
        const matchIdx = prev.findIndex(i =>
          i.stackable &&
          i.name === item.name &&
          i.category === item.category
        );
        if (matchIdx !== -1) {
          const target = prev[matchIdx];
          const tCap = (typeof target.maxStack === 'number' && target.maxStack > 0)
            ? target.maxStack : Infinity;
          const tQty = target.quantity || 1;
          const sQty = item.quantity || 1;
          const room = Math.max(0, tCap - tQty);
          const moved = Math.min(room, sQty);
          if (moved > 0) {
            const next = [...prev];
            next[matchIdx] = { ...target, quantity: tQty + moved };
            if (sQty - moved > 0) {
              next.push({ ...inventoryItem, quantity: sQty - moved });
            }
            didMerge = true;
            mergedQty = moved;
            systemBus.emit(EVENTS.INVENTORY_VAULT_TRANSFER, {
              item: inventoryItem,
              fromVault: vaultId,
              toSource: 'inventory',
              merged: true,
              mergedQty: moved,
            });
            return next;
          }
        }
      }

      systemBus.emit(EVENTS.INVENTORY_VAULT_TRANSFER, {
        item: inventoryItem,
        fromVault: vaultId,
        toSource: 'inventory',
        merged: false,
      });
      return [...prev, inventoryItem];
    });

    // [BATCH 4 / P1 D-23]: Direct logActivity — see transferToVault.
    const qty = item.quantity || 1;
    logActivity(
      `Retrieved ${qty}× ${item.name} from vault`,
      'items',
      { item: item.name, quantity: qty, vaultId, merged: didMerge, mergedQty }
    );
  }, [logActivity]);

  // ============================================================================
  // VAULT ACTIONS
  // ============================================================================

  /**
   * Add new vault
   * @param {Object} vault - Vault object to add
   */
  const addVault = useCallback((vault) => {
    setVaults(prev => {
      const newVault = {
        ...vault,
        id: vault.id || `vault_${Date.now()}_${Math.random()}`,
        items: vault.items || [],
        size: vault.size || [10, 10]
      };

      // [INTEGRATION]: Emit VAULT_CREATED event
      systemBus.emit(EVENTS.VAULT_CREATED, {
        vault: newVault
      });

      return [...prev, newVault];
    });
  }, []);

  /**
   * Remove vault
   * @param {string} vaultId - ID of vault to remove
   */
  const removeVault = useCallback((vaultId) => {
    setVaults(prev => {
      const vault = prev.find(v => v.id === vaultId);
      if (!vault) return prev;

      // [INTEGRATION]: Emit VAULT_REMOVED event
      systemBus.emit(EVENTS.VAULT_REMOVED, {
        vault
      });

      return prev.filter(v => v.id !== vaultId);
    });
  }, []);

  /**
   * Update vault properties
   * @param {string} vaultId - ID of vault to update
   * @param {Object} updates - Properties to update
   */
  const updateVault = useCallback((vaultId, updates) => {
    setVaults(prev => {
      const vaultIndex = prev.findIndex(v => v.id === vaultId);
      if (vaultIndex === -1) return prev;

      const updated = [...prev];
      updated[vaultIndex] = {
        ...updated[vaultIndex],
        ...updates
      };

      return updated;
    });
  }, []);

  // ============================================================================
  // CONTAINER ACTIONS
  // ============================================================================

  /**
   * Toggle container expanded state
   * @param {string} containerId - ID of container to toggle
   */
  const toggleContainer = useCallback((containerId) => {
    setExpandedContainers(prev => {
      const updated = new Set(prev);
      if (updated.has(containerId)) {
        updated.delete(containerId);
      } else {
        updated.add(containerId);
      }
      return updated;
    });
  }, []);

  /**
   * Expand container
   * @param {string} containerId - ID of container to expand
   */
  const expandContainer = useCallback((containerId) => {
    setExpandedContainers(prev => {
      const updated = new Set(prev);
      updated.add(containerId);
      return updated;
    });
  }, []);

  /**
   * Collapse container
   * @param {string} containerId - ID of container to collapse
   */
  const collapseContainer = useCallback((containerId) => {
    setExpandedContainers(prev => {
      const updated = new Set(prev);
      updated.delete(containerId);
      return updated;
    });
  }, []);

  /**
   * Collapse all containers
   */
  const collapseAllContainers = useCallback(() => {
    setExpandedContainers(new Set());
  }, []);

  // ============================================================================
  // DRAG & DROP ACTIONS
  // ============================================================================

  /**
   * Start dragging item
   * @param {Object} item - Item being dragged
   * @param {string} source - Source of drag (inventory, vault, container, etc.)
   */
  const startDrag = useCallback((item, source) => {
    setDraggedItem(item);
    setDragSource(source);
    dragDataRef.current = {
      item,
      source,
      isDragging: true
    };
  }, []);

  /**
   * End drag operation
   */
  const endDrag = useCallback(() => {
    setDraggedItem(null);
    setDragSource(null);
    setDragPosition(null);
    dragDataRef.current = {
      item: null,
      source: null,
      isDragging: false
    };
  }, []);

  /**
   * Update drag position
   * @param {Array} position - Current [x, y] position
   */
  const updateDragPosition = useCallback((position) => {
    setDragPosition(position);
  }, []);

  // ============================================================================
  // UTILITY FUNCTIONS
  // ============================================================================

  /**
   * Get item details from BASE_ITEMS database
   * [PURPOSE]: Look up base item data by type and category
   * [WHY]: Many functions need item properties from the database
   */
  const getItemDetails = useCallback((item) => {
    if (!item) return null;

    const itemType = item.type || item.baseType;
    const itemCategory = item.category;

    let baseData = null;
    if (itemCategory && BASE_ITEMS[itemCategory] && BASE_ITEMS[itemCategory][itemType]) {
      baseData = BASE_ITEMS[itemCategory][itemType];
    } else {
      // Fallback: search all categories
      for (const category of Object.keys(BASE_ITEMS)) {
        if (BASE_ITEMS[category][itemType]) {
          baseData = BASE_ITEMS[category][itemType];
          break;
        }
      }
    }

    return baseData;
  }, []);

  /**
   * Check if item can be equipped
   * [PURPOSE]: Validate item is equippable before showing equip options
   * [D&D 5E]: Weapons, armor, magical items, and wearable items can be equipped
   * [FIX NOV 28, 2025]: Expanded to include clothes, tools (held), and items with equipSlot
   */
  const canEquip = useCallback((item) => {
    const baseData = getItemDetails(item);
    const itemType = item.type || item.baseType;

    if (!baseData) return false;

    const isWeapon = item.category === 'weapons' || BASE_ITEMS.weapons?.[itemType];
    const isArmor = item.category === 'armor' || BASE_ITEMS.armor?.[itemType];
    const isMagical = item.category === 'magical_items' || BASE_ITEMS.magical_items?.[itemType];
    const isTool = item.category === 'tools' || BASE_ITEMS.tools?.[itemType];

    // [FIX NOV 28, 2025]: Clothes are wearable items in the 'items' category
    const isClothing = itemType?.startsWith('clothes_') ||
      ['clothes_common', 'clothes_fine', 'clothes_travelers', 'clothes_costume'].includes(itemType);

    // [FIX NOV 28, 2025]: Some items can be held (torch, lantern, holy symbol, etc.)
    const isHoldable = ['torch', 'lantern_hooded', 'lantern_bullseye', 'holy_symbol_amulet',
      'holy_symbol_emblem', 'holy_symbol_reliquary', 'arcane_focus_crystal', 'arcane_focus_orb',
      'arcane_focus_rod', 'arcane_focus_staff', 'arcane_focus_wand', 'druidic_focus_mistletoe',
      'druidic_focus_totem', 'druidic_focus_staff', 'druidic_focus_wand'].includes(itemType);

    return isWeapon || isArmor || isMagical || isTool || isClothing || isHoldable || item.equipSlot;
  }, [getItemDetails]);

  /**
   * Get valid equipment slots for an item
   * [PURPOSE]: Determine which slots an item can be equipped to
   * [D&D 5E]: Different item types have different valid slots
   */
  const getEquipSlots = useCallback((item) => {
    const baseData = getItemDetails(item);
    const itemType = item.type || item.baseType;

    if (!baseData) return [];

    const slots = [];

    // Weapons
    if (item.category === 'weapons' || BASE_ITEMS.weapons[itemType]) {
      slots.push('mainhand');
      if (baseData.properties?.includes('Light') || baseData.properties?.includes('Versatile')) {
        slots.push('offhand');
      }
      if (baseData.properties?.some(prop => prop.includes('Ammunition'))) {
        slots.push('ranged');
      }
    }

    // Armor
    if (item.category === 'armor' || BASE_ITEMS.armor[itemType]) {
      if (baseData.armorType === 'shield') {
        slots.push('shield');
      } else {
        slots.push('armor');
      }
    }

    // Magical Items
    // [SUB-PLAN 1R / 2026-05-29]: The previous unconditional `slots.push('mainhand')`
    //   fallback was a Rule 11 / Rule 17 anti-pattern — every BASE_ITEMS.magical_items
    //   row without an explicit equipSlot would silently be told "Equip to Main Hand".
    //   The 20 Ioun Stones (DMG p.176 + XGtE p.138) were the visible symptom: a player
    //   attunes an Ioun Stone and the UI tells them to wield it like a sword. Per the
    //   D&D 5e RAW, Ioun stones float around the head; they are never wielded.
    //
    //   The new dispatch is category-aware:
    //     1. If `baseData.equipSlot` is present, route to it (canonical path).
    //     2. Else if the item has weapon-like properties (a damage block or category
    //        === 'weapons'), fall back to 'mainhand' — preserves prior behavior for
    //        actual weapons that were registered under magical_items with no slot.
    //     3. Else: push 'misc' as a generic no-op indicator. UI consumers
    //        (ContextMenuComponent) treat 'misc' as a non-equippable wondrous-item
    //        signal and render an Inventory-only entry instead of an Equip button.
    //
    //   See .claude/findings/2026-05-29-bug-A-iounStone-equipSlot-label.md for the
    //   bug-investigator findings that led to this fix.
    if (BASE_ITEMS.magical_items && BASE_ITEMS.magical_items[itemType]) {
      if (baseData.equipSlot) {
        // [SUB-PLAN 1R-RENDERGAP STONE 1 / 2026-05-29]: Expand ring/earring bare slots
        // so magical-item ring entries (ring_of_protection, etc.) offer ring1+ring2.
        const expanded = expandRingEquipSlots(baseData.equipSlot);
        for (const s of expanded) {
          if (!slots.includes(s)) slots.push(s);
        }
      } else if (baseData.category === 'weapons' || baseData.damage) {
        slots.push('mainhand');
      } else {
        slots.push('misc');
      }
    }

    // [FIX NOV 28, 2025]: Clothes go to body slot
    const isClothing = itemType?.startsWith('clothes_') ||
      ['clothes_common', 'clothes_fine', 'clothes_travelers', 'clothes_costume'].includes(itemType);
    if (isClothing) {
      slots.push('body');
    }

    // [FIX NOV 28, 2025]: Tools and holdable items go to mainhand/offhand
    const isTool = item.category === 'tools' || BASE_ITEMS.tools?.[itemType];
    const isHoldable = ['torch', 'lantern_hooded', 'lantern_bullseye', 'holy_symbol_amulet',
      'holy_symbol_emblem', 'holy_symbol_reliquary', 'arcane_focus_crystal', 'arcane_focus_orb',
      'arcane_focus_rod', 'arcane_focus_staff', 'arcane_focus_wand', 'druidic_focus_mistletoe',
      'druidic_focus_totem', 'druidic_focus_staff', 'druidic_focus_wand'].includes(itemType);
    if (isTool || isHoldable) {
      slots.push('mainhand');
      slots.push('offhand');
    }

    // [SUB-PLAN 1R-RENDERGAP STONE 1 / 2026-05-29]: If item has explicit equipSlot,
    // use that — but expand ring/earring bare slots to their dual-slot array so the
    // ContextMenu can offer both ring1+ring2 (or earring1+earring2) as equip targets.
    // expandRingEquipSlots('ring') → ['ring1','ring2'] (both options)
    // expandRingEquipSlots('earring') → ['earring1','earring2']
    // expandRingEquipSlots('neck') → ['neck'] (pass-through for all other slots)
    // This is the runtime fix; on-load migration (migrateRingSlots) handles legacy
    // saves that already have equipSlot:'ring' in their equipped=true state.
    if (item.equipSlot && !slots.includes(item.equipSlot)) {
      const expanded = expandRingEquipSlots(item.equipSlot);
      for (const s of expanded) {
        if (!slots.includes(s)) slots.push(s);
      }
    }

    return slots;
  }, [getItemDetails]);

  // ============================================================================
  // BONUS MANAGEMENT FUNCTIONS
  // ============================================================================

  /**
   * Apply item bonuses when equipped
   * [PURPOSE]: Add stat modifications from equipped items to quick bonuses
   * [D&D 5E]: Items like Belt of Giant Strength SET stats, not add to them
   * [INTEGRATION]: Emits QUICK_BONUS_ADDED event, affects EffectCalculator
   */
  const applyItemBonus = useCallback((item) => {
    // Check if item has stat-setting properties from custom properties first
    if (item.statBonus && item.statValue) {
      const statName = item.statBonus.replace(/^(set|add|subtract)/, '');
      const statDisplayName = statName.charAt(0).toUpperCase() + statName.slice(1);
      let effect = null;

      if (item.statBonus.startsWith('set')) {
        effect = {
          name: item.name,
          bonus: item.statValue,
          affects: `${statDisplayName} (SET)`,
          duration: 'While equipped',
          icon: '💍',
          type: item.statBonus,
          value: item.statValue,
          itemId: item.id
        };
      } else if (item.statBonus.startsWith('add')) {
        effect = {
          name: item.name,
          bonus: item.statValue,
          affects: `${statDisplayName} (ADD)`,
          duration: 'While equipped',
          icon: '➕',
          type: item.statBonus,
          value: item.statValue,
          itemId: item.id
        };
      } else if (item.statBonus.startsWith('subtract')) {
        effect = {
          name: item.name,
          bonus: -Math.abs(item.statValue),
          affects: `${statDisplayName} (SUBTRACT)`,
          duration: 'While equipped',
          icon: '➖',
          type: item.statBonus,
          value: -Math.abs(item.statValue),
          itemId: item.id
        };
      }

      if (effect) {
        const alreadyApplied = activeQuickBonuses.some(b => b.itemId === item.id);
        if (!alreadyApplied) {
          setActiveQuickBonuses(prev => [...prev, {
            ...effect,
            id: Date.now(),
            startTime: Date.now()
          }]);

          systemBus.emit(EVENTS.QUICK_BONUS_ADDED, {
            bonusId: `item_${item.id}`,
            bonusName: item.name,
            bonusType: effect.affects,
            value: effect.bonus,
            duration: 'permanent',
            source: 'item_equipped'
          });

          const message = item.statBonus.startsWith('set')
            ? `${item.name} equipped - ${statDisplayName} set to ${item.statValue}`
            : item.statBonus.startsWith('add')
            ? `${item.name} equipped - +${item.statValue} to ${statDisplayName}`
            : `${item.name} equipped - -${Math.abs(item.statValue)} to ${statDisplayName}`;

          showStatus(message);
          logActivity(`Equipped ${item.name}`, 'items', { effect });
        }
        return;
      }
    }

    // [FIX JAN 13, 2026]: Property-based detection via effects array instead of string matching (ARC-014)
    // Check for ability-setting effects in item.effects array
    const abilityEffect = item.effects?.find(e => e.type === 'ability' && e.operation === 'set');
    if (abilityEffect) {
      const abilityNames = {
        str: 'Strength',
        dex: 'Dexterity',
        con: 'Constitution',
        int: 'Intelligence',
        wis: 'Wisdom',
        cha: 'Charisma'
      };
      const abilityName = abilityNames[abilityEffect.target] || abilityEffect.target;
      const effect = {
        name: item.name,
        bonus: abilityEffect.value,
        affects: `${abilityName} (SET)`,
        duration: 'While equipped',
        icon: item.icon || '✨',
        type: `set${abilityName}`,
        value: abilityEffect.value,
        itemId: item.id
      };

      const alreadyApplied = activeQuickBonuses.some(b => b.itemId === item.id);
      if (!alreadyApplied) {
        setActiveQuickBonuses(prev => [...prev, { ...effect, id: Date.now(), startTime: Date.now() }]);
        systemBus.emit(EVENTS.QUICK_BONUS_ADDED, { bonusId: `item_${item.id}`, bonusName: item.name, bonusType: effect.affects, value: effect.bonus, duration: 'permanent', source: 'item_equipped' });
        showStatus(`${item.name} equipped - ${effect.affects} set to ${effect.bonus}`);
        logActivity(`Equipped ${item.name}`, 'items', { effect });
      }
    }
  }, [activeQuickBonuses, setActiveQuickBonuses, showStatus, logActivity]);

  /**
   * Remove item bonuses when unequipped
   * [PURPOSE]: Remove stat modifications from quick bonuses
   * [INTEGRATION]: Emits QUICK_BONUS_REMOVED event
   */
  const removeItemBonus = useCallback((itemId) => {
    const removedBonus = activeQuickBonuses.find(b => b.itemId === itemId);
    if (removedBonus) {
      systemBus.emit(EVENTS.QUICK_BONUS_REMOVED, {
        bonusId: `item_${itemId}`,
        bonusName: removedBonus.name || 'Unknown Item',
        reason: 'item_unequipped'
      });
    }
    setActiveQuickBonuses(prev => prev.filter(b => b.itemId !== itemId));
  }, [activeQuickBonuses, setActiveQuickBonuses]);

  // ============================================================================
  // EQUIPMENT MANAGEMENT FUNCTIONS
  // ============================================================================

  /**
   * Equip item to a specific slot
   * [PURPOSE]: Equip weapons, armor, and magical items
   * [D&D 5E]: Only one item per slot, automatically unequips previous item
   * [INTEGRATION]: Emits ITEM_EQUIPPED/UNEQUIPPED events, clears EffectCalculator cache
   */
  const equipItem = useCallback((item, slot) => {
    let unequippedItem = null;

    setInventory(prev => {
      const updatedInv = prev.map(i => {
        if (i.equipped && i.equipSlot === slot) {
          removeItemBonus(i.id);
          // [PHASE 1.3 - NOV 14, 2025]: Unregister item effects from EffectRegistry
          if (i.effects && Array.isArray(i.effects)) {
            try {
              effectRegistry.remove(`item:${i.id}:${slot}`);
            } catch (error) {
              console.warn(`[InventoryContext] Failed to unregister effects for ${i.name}:`, error);
            }
          }
          logActivity(`Unequipped ${i.name} from ${slot}`, 'items', { item: i, slot });
          unequippedItem = i;
          return { ...i, equipped: false, equipSlot: null };
        }
        if (i.id === item.id) {
          logActivity(`Equipped ${item.name} to ${slot}`, 'items', { item, slot });
          return { ...i, equipped: true, equipSlot: slot };
        }
        return i;
      });

      setTimeout(() => {
        const equippedItem = updatedInv.find(i => i.id === item.id);
        if (equippedItem && equippedItem.equipped) {
          applyItemBonus(equippedItem);
          // [PHASE 1.3 - NOV 14, 2025]: Register item effects with EffectRegistry
          if (equippedItem.effects && Array.isArray(equippedItem.effects)) {
            try {
              effectRegistry.register(`item:${equippedItem.id}:${slot}`, equippedItem.effects, equippedItem);
            } catch (error) {
              console.warn(`[InventoryContext] Failed to register effects for ${equippedItem.name}:`, error);
            }
          }
        }
      }, 0);

      return updatedInv;
    });

    setContextMenu(null);
    showStatus(`Equipped ${item.name} to ${slot}`);

    if (unequippedItem) {
      systemBus.emit(EVENTS.ITEM_UNEQUIPPED, {
        item: unequippedItem,
        slot,
        statChanges: unequippedItem.statModifications || null
      });
    }

    systemBus.emit(EVENTS.ITEM_EQUIPPED, {
      item,
      slot,
      previousItem: unequippedItem,
      statChanges: item.statModifications || null
    });

    effectCalculator.clearCache();
  }, [removeItemBonus, logActivity, applyItemBonus, setContextMenu, showStatus]);

  /**
   * Unequip item from its slot
   * [PURPOSE]: Remove equipped items
   * [INTEGRATION]: Emits ITEM_UNEQUIPPED event, clears EffectCalculator cache
   */
  const unequipItem = useCallback((item) => {
    const slot = item.equipSlot;

    setInventory(prev => prev.map(i => {
      if (i.id === item.id) {
        removeItemBonus(item.id);
        // [PHASE 1.3 - NOV 14, 2025]: Unregister item effects from EffectRegistry
        if (item.effects && Array.isArray(item.effects)) {
          try {
            effectRegistry.remove(`item:${item.id}:${slot}`);
          } catch (error) {
            console.warn(`[InventoryContext] Failed to unregister effects for ${item.name}:`, error);
          }
        }
        logActivity(`Unequipped ${item.name}`, 'items', { item });
        return { ...i, equipped: false, equipSlot: null };
      }
      return i;
    }));

    showStatus(`Unequipped ${item.name}`);

    systemBus.emit(EVENTS.ITEM_UNEQUIPPED, {
      item,
      slot,
      statChanges: item.statModifications || null
    });

    effectCalculator.clearCache();
  }, [removeItemBonus, logActivity, showStatus]);

  /**
   * Attune to a magical item
   * [PURPOSE]: Attune to magical items (required for some effects)
   * [D&D 5E]: Max 3 attuned items, short rest required to attune/unattune
   * [INTEGRATION]: Emits ITEM_ATTUNED event, clears EffectCalculator cache
   */
  const attuneToItem = useCallback((item) => {
    const currentlyAttuned = inventory.filter(i => i.attuned).length;
    // [STRUCT-C 2026-05-16]: Replace hardcoded `>= 3` with data-driven cap.
    //   Artificer L10/L14/L18 raises cap to 4/5/6 (TCoE p.11-12 Magic Item
    //   Adept / Savant / Master). Helper reads playerStats.classes.
    const maxAttunement = getMaxAttunement(playerStats);

    if (currentlyAttuned >= maxAttunement) {
      showStatus(`❌ You can only attune to ${maxAttunement} items at once. Unattune another item first.`);
      return;
    }

    if (!item.requiresAttunement) {
      showStatus(`${item.name} does not require attunement.`);
      return;
    }

    // [STRUCT-C 2026-05-16]: Honor `attunementRequirement` data field if set
    //   on the item (RAW DMG p.138 — "requires attunement by a wizard" etc.).
    //   Artificer L14 Magic Item Savant bypasses all such requirements per
    //   TCoE p.12; the helper reads the artificer_magic_item_savant_ignore_reqs
    //   flag from effectRegistry.
    if (item.attunementRequirement
        && !meetsAttunementRequirement(playerStats, inventory, item.attunementRequirement)) {
      showStatus(`❌ ${item.name} requires attunement by a ${item.attunementRequirement}.`);
      return;
    }

    setInventory(prev => prev.map(i => {
      if (i.id === item.id) {
        logActivity(`Attuned to ${item.name}`, 'items', { item });
        return { ...i, attuned: true };
      }
      return i;
    }));

    showStatus(`🔮 Attuned to ${item.name} (${currentlyAttuned + 1}/${maxAttunement} slots used)`);

    systemBus.emit(EVENTS.ITEM_ATTUNED, {
      item,
      attunementSlot: currentlyAttuned + 1,
      requiresAttunement: item.requiresAttunement
    });

    effectCalculator.clearCache();
  }, [inventory, playerStats, logActivity, showStatus]);

  /**
   * Remove attunement from an item
   * [PURPOSE]: Free up attunement slot
   * [D&D 5E]: Frees up one of 3 attunement slots; cursed items lock (DMG p.138)
   * [INTEGRATION]: Emits ITEM_UNATTUNED event, clears EffectCalculator cache
   *
   * [SUB-PLAN 1C ITEM 4 / 2026-05-23]: Cursed-attunement lock — RAW DMG p.138
   *   says "Attunement to a cursed item can't be ended voluntarily unless the
   *   curse is broken first, such as with the *remove curse* spell." The gate
   *   short-circuits with a status message before any state mutation; the
   *   activity log is intentionally NOT touched for blocked attempts (the
   *   user receives feedback via showStatus only).
   */
  const unattuneItem = useCallback((item) => {
    // [SUB-PLAN 1C ITEM 4]: Block voluntary un-attune from cursed items.
    if (!canUnattuneItem(item)) {
      showStatus(getCurseUnattuneBlockedMessage(item));
      return;
    }

    setInventory(prev => prev.map(i => {
      if (i.id === item.id) {
        if (i.equipped) {
          removeItemBonus(item.id);
        }
        logActivity(`Unattuned from ${item.name}`, 'items', { item });
        return { ...i, attuned: false };
      }
      return i;
    }));

    const remainingAttuned = inventory.filter(i => i.attuned && i.id !== item.id).length;
    // [STRUCT-C 2026-05-16]: Use data-driven cap (Artificer scaling).
    const maxAttunement = getMaxAttunement(playerStats);
    showStatus(`Unattuned from ${item.name} (${remainingAttuned}/${maxAttunement} slots used)`);

    systemBus.emit(EVENTS.ITEM_UNATTUNED, {
      item,
      attunementSlot: remainingAttuned + 1,
      reason: 'manual'
    });

    effectCalculator.clearCache();
  }, [inventory, playerStats, removeItemBonus, logActivity, showStatus]);

  /**
   * Identify an unidentified item
   * [PURPOSE]: Flip `isIdentified` from false → true; reveals the item's
   *   real name + description in the inventory + magic-item tab surfaces.
   * [D&D 5E]: Maps to the Identify spell (PHB p.252) or "spend a short rest
   *   in contact with the item" (DMG p.136) — both fully reveal magical
   *   properties EXCEPT curses (DMG p.138, see cursed-item gate for the
   *   parallel rule). For v1 minimum we don't enforce the spell/rest gate
   *   — manual action only; resource gating deferred to v1.x.
   * [INTEGRATION]: Emits `ITEM_IDENTIFIED` event (logged to systemBus) and
   *   appends an activity-log entry (Rule 13).
   *
   * [SUB-PLAN 1C ITEM 3 / 2026-05-23]: No-op when the item is already
   *   identified (defensive; avoids spammy log noise from double-clicks).
   */
  const identifyItem = useCallback((item) => {
    if (!item || isItemIdentified(item)) {
      // Already identified or invalid — silent no-op.
      return;
    }
    setInventory(prev => prev.map(i => {
      if (i.id !== item.id) return i;
      return { ...i, isIdentified: true };
    }));
    showStatus(`🔍 You identify the ${item.name || 'item'} — its properties are revealed.`);
    logActivity(`Identified ${item.name || 'unknown item'}`, 'items', { item });
    systemBus.emit(EVENTS.ITEM_IDENTIFIED, { item });
    effectCalculator.clearCache();
  }, [logActivity, showStatus]);

  /**
   * Consume ammunition when firing a ranged weapon
   * [PURPOSE]: Track ammunition usage for ranged weapons
   * [D&D 5E]: Each attack consumes 1 piece of ammunition
   * [INTEGRATION]: Emits INVENTORY_ITEM_REMOVED event
   *
   * [BATCH 4 / P0 C-3 — APR 2026]: Three independent bugs were compounding:
   *   1. `inventory.find(item => item.type === ammoType)` never matched —
   *      real InventoryItem instances don't carry a `type` field, so every
   *      ranged attack via EquipmentPanel returned "Out of arrows!" even
   *      with a full quiver.
   *   2. The if/else chain checked `weaponName.includes('bow')` before the
   *      crossbow branch. 'crossbow' contains 'bow' → crossbows always got
   *      'arrows' and the crossbow_bolts branch was unreachable.
   *   3. The decrement step removed the entire stack via
   *      `prev.filter(item => item.id !== ammoItem.id)`. Firing 1 arrow
   *      from a stack of 20 left the player with 0.
   *
   * Fix: data-driven matching. Weapon's BASE_ITEMS data carries `ammoType`
   *   (e.g., longbow.ammoType = 'arrows'); ammunition's BASE_ITEMS data
   *   also carries `ammoType` (arrows.ammoType = 'arrows'); InventoryItem
   *   propagates ammoType per Rule 9. Match by `item.ammoType` and
   *   decrement quantity (or remove when 0).
   */
  const consumeAmmunition = useCallback((weapon, weaponData) => {
    const requiresAmmo = weaponData?.properties?.some(prop =>
      prop.toLowerCase().includes('ammunition')
    );

    if (!requiresAmmo) {
      return { success: true, remaining: Infinity, message: 'No ammunition required' };
    }

    // [DATA-DRIVEN]: Read ammoType from the weapon's BASE_ITEMS data.
    // Defensive fallback: if a legacy save / custom weapon lacks the field,
    // fall back to a tightened name-match (crossbow checked BEFORE bow,
    // because 'crossbow' contains 'bow').
    let ammoType = weaponData?.ammoType || weapon?.ammoType || null;
    if (!ammoType) {
      const weaponName = (weapon?.name || weaponData?.name || '').toLowerCase();
      if (weaponName.includes('crossbow')) ammoType = 'crossbow_bolts';
      else if (weaponName.includes('bow')) ammoType = 'arrows';
      else if (weaponName.includes('sling')) ammoType = 'sling_bullets';
      else if (weaponName.includes('blowgun')) ammoType = 'blowgun_needles';
    }

    if (!ammoType) {
      return { success: false, remaining: 0, message: '⚠️ Unknown ammunition type for this weapon' };
    }

    const ammoItem = inventory.find(item => item.ammoType === ammoType && !item.equipped);

    if (!ammoItem) {
      return { success: false, remaining: 0, message: `❌ Out of ${ammoType.replace('_', ' ')}! Cannot attack.` };
    }

    // [STACK-AWARE DECREMENT]: Decrement quantity by 1; remove the entry
    // only when the stack is exhausted. Was previously dropping the whole
    // stack via `prev.filter(...id !== ammoItem.id)`.
    const currentQty = ammoItem.quantity || 1;
    const willEmpty = currentQty <= 1;

    setInventory(prev => {
      if (willEmpty) {
        return prev.filter(item => item.id !== ammoItem.id);
      }
      return prev.map(item =>
        item.id === ammoItem.id
          ? { ...item, quantity: currentQty - 1 }
          : item
      );
    });

    systemBus.emit(EVENTS.INVENTORY_ITEM_REMOVED, {
      item: ammoItem,
      quantity: 1,
      reason: 'consumed'
    });

    logActivity(`Consumed 1 ${ammoType.replace('_', ' ')}`, 'combat', {
      weapon: weapon.name,
      ammo: ammoType
    });

    // Sum remaining quantity across all stacks of this ammo type
    // (subtract the 1 we just consumed; the closure's `inventory` is the
    // pre-decrement snapshot).
    const remainingAmmo = inventory
      .filter(item => item.ammoType === ammoType)
      .reduce((sum, item) => sum + (item.quantity || 1), 0) - 1;

    // [SPEC C TASK 3 — 2026-05-17]: Return the consumed ammoItem alongside
    //   the existing success/remaining/message so callers (EquipmentPanel's
    //   on_hit firer) can pass it into the trigger context. Pre-this-change
    //   the consumed ammo was scoped inside this helper and unreachable from
    //   rollWeaponAttack, blocking ammoTypeMatch filters on +1 Arrow / Arrow
    //   of Slaying triggeredEffect declarations (Task 9 ammo migration).
    //   Returning a SNAPSHOT of the pre-decrement item — callers use it for
    //   context only (not for further mutation).
    if (remainingAmmo === 0) {
      return { success: true, remaining: 0, ammoItem, message: `⚠️ That was your last ${ammoType.replace('_', ' ')}!` };
    } else if (remainingAmmo <= 2) {
      return { success: true, remaining: remainingAmmo, ammoItem, message: `⚠️ Low on ${ammoType.replace('_', ' ')} (${remainingAmmo} remaining)` };
    }

    return { success: true, remaining: remainingAmmo, ammoItem, message: `${remainingAmmo} ${ammoType.replace('_', ' ')} remaining` };
  }, [inventory, logActivity]);

  // ============================================================================
  // ITEM ACTION FUNCTIONS
  // ============================================================================

  /**
   * Sell item for currency
   * [PURPOSE]: Convert items to gold and silver based on sell ratio
   * [D&D 5E]: Typical sell ratio is 50% of base value
   * [INTEGRATION]: Updates currency, emits CURRENCY_CHANGED and INVENTORY_ITEM_REMOVED events
   */
  const sellItem = useCallback((item) => {
    // Calculate total value (including container contents if applicable)
    const getTotalValue = () => {
      let total = (item.value || 0) * (item.quantity || 1);
      if (item.isContainer && item.containedItems) {
        item.containedItems.forEach(contained => {
          total += (contained.value || 0) * (contained.quantity || 1);
        });
      }
      return total;
    };

    const totalValue = getTotalValue();
    const sellValue = totalValue * (settings?.defaultSellRatio || 0.5);
    const goldValue = Math.floor(sellValue);
    const silverValue = Math.floor((sellValue - goldValue) * 10);

    const oldGold = currencies.gp || 0;
    const oldSilver = currencies.sp || 0;
    const newGold = Math.round(oldGold + goldValue);
    const newSilver = Math.round(oldSilver + silverValue);

    setCurrencies(prev => ({
      ...prev,
      gp: newGold,
      sp: newSilver
    }));

    if (goldValue > 0) {
      systemBus.emit(EVENTS.CURRENCY_CHANGED, {
        type: 'gp',
        oldAmount: oldGold,
        newAmount: newGold,
        change: goldValue,
        source: 'item_sale'
      });
    }
    if (silverValue > 0) {
      systemBus.emit(EVENTS.CURRENCY_CHANGED, {
        type: 'sp',
        oldAmount: oldSilver,
        newAmount: newSilver,
        change: silverValue,
        source: 'item_sale'
      });
    }

    setInventory(prev => prev.filter(i => i.id !== item.id));

    systemBus.emit(EVENTS.INVENTORY_ITEM_REMOVED, {
      item: item,
      quantity: 1,
      reason: 'sold'
    });

    setContextMenu(null);

    if (item.isContainer && item.containedItems?.length > 0) {
      showStatus(`Sold ${item.name} and ${item.containedItems.length} contained items for ${goldValue}gp ${silverValue}sp`);
      logActivity(`Sold ${item.name} and contents for ${goldValue}gp ${silverValue}sp`, 'currency', {
        item: item.name,
        totalValue,
        goldEarned: goldValue,
        silverEarned: silverValue,
        containedItems: item.containedItems?.length || 0
      });
    } else {
      logActivity(`Sold ${item.name} for ${goldValue}gp ${silverValue}sp`, 'currency', {
        item: item.name,
        totalValue,
        goldEarned: goldValue,
        silverEarned: silverValue
      });
    }
  }, [settings, currencies, setCurrencies, setContextMenu, showStatus, logActivity]);

  /**
   * Delete item from inventory or container
   * [PURPOSE]: Permanently remove items
   * [INTEGRATION]: Emits INVENTORY_ITEM_REMOVED event
   */
  const deleteItem = useCallback((item, source) => {
    // [FIX P4-12]: Return contained currency before deleting item
    // [WHY]: Items like pouches can contain gold — deleting them without recovery loses currency
    if (item.containsCurrency) {
      const { amount, type } = item.containsCurrency;
      if (amount && type) {
        setCurrencies(prev => ({
          ...prev,
          [type]: (prev[type] || 0) + amount
        }));
        logActivity(`Recovered ${amount} ${type} from deleted ${item.name}`, 'currency', {
          amount, type, item: item.name
        });
      }
    }

    if (source === 'inventory') {
      setInventory(prev => prev.filter(i => i.id !== item.id));

      systemBus.emit(EVENTS.INVENTORY_ITEM_REMOVED, {
        item: item,
        quantity: 1,
        reason: 'destroyed'
      });

      logActivity(`Deleted ${item.name} from inventory`, 'items', {
        item: item.name,
        source,
        value: item.value,
        weight: item.weight
      });
    } else if (source.startsWith('container_')) {
      const containerId = source.replace('container_', '');
      setInventory(prev => prev.map(i => {
        if (i.id === containerId) {
          return {
            ...i,
            containedItems: i.containedItems.filter(ci => ci.id !== item.id)
          };
        }
        return i;
      }));
      logActivity(`Deleted ${item.name} from container`, 'items', {
        item: item.name,
        source,
        containerId
      });
    } else {
      // Vault deletion
      const vaultId = source.replace('vault_', '');
      setVaults(prev => prev.map(v => {
        if (v.id === vaultId) {
          return {
            ...v,
            items: v.items.filter(i => i.id !== item.id)
          };
        }
        return v;
      }));
      logActivity(`Deleted ${item.name} from vault`, 'items', {
        item: item.name,
        source,
        vaultId
      });
    }

    setContextMenu(null);
  }, [logActivity, setContextMenu, setCurrencies]);

  // ============================================================================
  // CONTEXT VALUE
  // ============================================================================

  // [FIX APR 2026 — AUDIT C-4]: Memoize context value to prevent unnecessary consumer re-renders
  const value = useMemo(() => ({
    // Core State
    inventory,
    setInventory,
    vaults,
    setVaults,

    // Drag & Drop State
    draggedItem,
    setDraggedItem,
    dragSource,
    setDragSource,
    dragPosition,
    setDragPosition,
    selectedItem,
    setSelectedItem,
    selectedItemSource,
    setSelectedItemSource,
    hoveredGrid,
    setHoveredGrid,
    hoveredCell,
    setHoveredCell,
    dragDataRef,

    // Container & Vault State
    openContainer,
    setOpenContainer,
    showVaultModal,
    setShowVaultModal,
    expandedContainers,
    setExpandedContainers,
    editingVault,
    setEditingVault,
    directAddToVault,
    setDirectAddToVault,

    // Item Management State
    editingItem,
    setEditingItem,
    showItemDialog,
    setShowItemDialog,
    showVaultDialog,
    setShowVaultDialog,
    contextMenu,
    setContextMenu,

    // Sorting & Filtering State
    sortBy,
    setSortBy,
    sortOrder,
    setSortOrder,
    filterType,
    setFilterType,
    searchQuery,
    setSearchQuery,
    magicItemsFilter,
    setMagicItemsFilter,
    magicItemsSort,
    setMagicItemsSort,

    // Inventory Actions
    addItem,
    removeItem,
    updateItem,
    moveItem,
    transferToVault,
    transferFromVault,

    // Stack Actions
    splitStack,
    makeStackable,
    combineStacks,
    mergeStackOnto,

    // Vault Actions
    addVault,
    removeVault,
    updateVault,

    // Container Actions
    toggleContainer,
    expandContainer,
    collapseContainer,
    collapseAllContainers,

    // Drag & Drop Actions
    startDrag,
    endDrag,
    updateDragPosition,

    // Utility Functions
    getItemDetails,
    canEquip,
    getEquipSlots,

    // Bonus Management
    applyItemBonus,
    removeItemBonus,

    // Equipment Management
    equipItem,
    unequipItem,
    attuneToItem,
    unattuneItem,
    consumeAmmunition,
    identifyItem, // [SUB-PLAN 1C ITEM 3 / 2026-05-23]: Reveal unidentified loot

    // Item Actions
    sellItem,
    deleteItem
  }), [
    // State values
    inventory, vaults,
    draggedItem, dragSource, dragPosition, selectedItem, selectedItemSource,
    hoveredGrid, hoveredCell, dragDataRef,
    openContainer, showVaultModal, expandedContainers, editingVault, directAddToVault,
    editingItem, showItemDialog, showVaultDialog, contextMenu,
    sortBy, sortOrder, filterType, searchQuery, magicItemsFilter, magicItemsSort,
    // Stable references (setters and useCallback functions — included for ESLint exhaustive-deps)
    setInventory, setVaults,
    setDraggedItem, setDragSource, setDragPosition, setSelectedItem, setSelectedItemSource,
    setHoveredGrid, setHoveredCell,
    setOpenContainer, setShowVaultModal, setExpandedContainers, setEditingVault, setDirectAddToVault,
    setEditingItem, setShowItemDialog, setShowVaultDialog, setContextMenu,
    setSortBy, setSortOrder, setFilterType, setSearchQuery, setMagicItemsFilter, setMagicItemsSort,
    addItem, removeItem, updateItem, moveItem, transferToVault, transferFromVault,
    splitStack, makeStackable, combineStacks, mergeStackOnto,
    addVault, removeVault, updateVault,
    toggleContainer, expandContainer, collapseContainer, collapseAllContainers,
    startDrag, endDrag, updateDragPosition,
    getItemDetails, canEquip, getEquipSlots,
    applyItemBonus, removeItemBonus,
    equipItem, unequipItem, attuneToItem, unattuneItem, consumeAmmunition, identifyItem,
    sellItem, deleteItem
  ]);

  return (
    <InventoryContext.Provider value={value}>
      {children}
    </InventoryContext.Provider>
  );
};

/**
 * Hook to access InventoryContext
 * @throws {Error} if used outside InventoryProvider
 *
 * [FIX - NOV 3, 2025]: Enhanced error message with debugging hints
 */
export const useInventory = () => {
  const context = useContext(InventoryContext);
  if (!context) {
    console.error('❌ useInventory() called outside InventoryProvider!');
    console.error('📍 Check that your component is wrapped in <AppProviders>');
    console.error('📍 File: src/contexts/InventoryContext.js');
    throw new Error(
      'useInventory must be used within InventoryProvider. ' +
      'Ensure your component is wrapped in <AppProviders>. ' +
      'See src/contexts/InventoryContext.js:1190'
    );
  }
  return context;
};

export default InventoryContext;
